import { useState, useEffect } from "react";
import {
  ScrollableContent,
  ModalHeader,
} from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import Config from "../../Config";
import { useMutation } from "react-query";
import DateRangePicker from "../Common/DateRangePicker";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

import { Line } from "react-chartjs-2";
import { FiClipboard } from "react-icons/fi";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const AnalyticsSideModal = ({
  token,
  enabled,
  handleClose,
}) => {
  const [height, setHeight] = useState(window.innerHeight);
  const [apiResp, setApiResp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  //const [error, setError] = useState(false);
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment().endOf("month").format("YYYY-MM-DD")
  );
  const submitHandler = (values) => {
    setStartDate(moment(values.startDate).format("YYYY-MM-DD"));
    setEndDate(moment(values.endDate).format("YYYY-MM-DD"));
  };
  /* ================= API CALL ================= */

  const fetchFunction = async () => {
    const response = await axios.get(
      `${Config.apiUrl}/client/analytics?startDate=${startDate}&endDate=${endDate}&term=1&excludeVacantBeds=${enabled === false ? 0 : 1
      }`,
      {
        headers: {
          authorization: "Bearer" + " " + token,
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        },
      }
    );
    return response.data;
  };

  const getSuccess = (res) => {
    if (res.isSuccess === false) error = true;
    else {
      setApiResp(res);
    }
  };

  const getError = (res) => { };

  const {
    isLoading,
    mutate: getListMutate,
    error,
    data,
    isRefetching,
  } = useMutation(fetchFunction, {
    onSuccess: getSuccess,
    onError: getError,
  });

  useEffect(() => {
    if (token && startDate && endDate) {
      getListMutate();
    }
  }, [token, startDate, endDate, enabled]);

  /* ================= Helpers ================= */

  const calculateTrend = (current, previous) => {
    const curr = Number(current || 0);
    const prev = Number(previous || 0);

    if (prev === 0) {
      if (curr === 0) return 0;
      return 100; // if previously 0 and now positive
    }

    return ((curr - prev) / prev) * 100;
  };

  const formatCurrency = (val) =>
    `₹${val && val !== "NaN" ? Number(val).toFixed(2) : "0.00"
    }`;

  const formatPercent = (val) =>
    `${val ? Number(val).toFixed(2) : 0}%`;

  const getTrendUI = (percent, reverse = false) => {
    const value = Number(percent || 0);

    if (value === 0) {
      return {
        arrow: "–",
        color: "text-gray-400",
        value: "0.0",
      };
    }

    const isIncrease = value > 0;

    // In reverse mode (MoveOut), increase is BAD
    const isGood = reverse ? value < 0 : value > 0;

    return {
      arrow: isIncrease ? "▲" : "▼",
      color: isGood ? "text-green-600" : "text-red-500",
      value: Math.abs(value).toFixed(1),
    };
  };
  const newTenantsMoM = calculateTrend(
    apiResp?.tenantStats?.newTenants,
    apiResp?.tenantStats?.newTenantsLastPeriod
  );

  const newTenantsYoY = calculateTrend(
    apiResp?.tenantStats?.newTenants,
    apiResp?.tenantStats?.newTenantsLastYear
  );

  const moveOutMoM = calculateTrend(
    apiResp?.tenantStats?.moveOutTenants,
    apiResp?.tenantStats?.moveOutTenantsLastPeriod
  );

  const moveOutYoY = calculateTrend(
    apiResp?.tenantStats?.moveOutTenants,
    apiResp?.tenantStats?.moveOutTenantsLastYear
  );

  const cancelledMoM = calculateTrend(
    apiResp?.tenantStats?.cancelledReserveTenants,
    apiResp?.tenantStats?.cancelledReserveTenantsLastPeriod
  );

  const cancelledYoY = calculateTrend(
    apiResp?.tenantStats?.cancelledReserveTenants,
    apiResp?.tenantStats?.cancelledReserveTenantsLastYear
  );

  const revenueChartData = {
    labels: apiResp?.monthlyRevenuePerTenant?.map(
      (item) => moment(`${item.year}-${item.month}-01`).format("MMM YY")
    ) || [],

    datasets: [
      {
        label: "Revenue per Tenant",
        data:
          apiResp?.monthlyRevenuePerTenant?.map((item) =>
            Number(item.revenue)
          ) || [],
        borderColor: "#2563eb",
        backgroundColor: "rgba(37,99,235,0.1)",
        tension: 0.35,
        fill: true,
      },
    ],
  };

  const profitChartData = {
    labels: apiResp?.monthlyProfitPerBed?.map(
      (item) => moment(`${item.year}-${item.month}-01`).format("MMM YY")
    ) || [],

    datasets: [
      {
        label: "All Beds Profit",
        data:
          apiResp?.monthlyProfitPerBed?.map((item) =>
            Number(item.allBedProfit)
          ) || [],
        borderColor: "#10b981",
        backgroundColor: "rgba(16,185,129,0.1)",
        tension: 0.35,
        fill: true,
      },
      {
        label: "Occupied Beds Profit",
        data:
          apiResp?.monthlyProfitPerBed?.map((item) =>
            Number(item.occupiedBedProfit)
          ) || [],
        borderColor: "#f59e0b",
        backgroundColor: "rgba(245,158,11,0.1)",
        tension: 0.35,
        fill: true,
      },
    ],
  };

  /* ================= Metrics ================= */

  const tenantMetrics = [
    {
      title: "New Tenants",
      value: apiResp?.tenantStats?.newTenants || 0,
      trends: [
        { label: "MoM", ...getTrendUI(newTenantsMoM) },
        { label: "YoY", ...getTrendUI(newTenantsYoY) },
      ],
    },
    {
      title: "Moved Out Tenants",
      value: apiResp?.tenantStats?.moveOutTenants || 0,
      trends: [
        { label: "MoM", ...getTrendUI(moveOutMoM, true) },
        { label: "YoY", ...getTrendUI(moveOutYoY, true) },
      ],
    },
    {
      title: "Cancelled Bookings",
      value: apiResp?.tenantStats?.cancelledReserveTenants || 0,
      trends: [
        { label: "MoM", ...getTrendUI(cancelledMoM, true) },
        { label: "YoY", ...getTrendUI(cancelledYoY, true) },
      ],
    },
  ];

  const financialMetrics = [
    {
      title: "Profit per Bed",
      value: formatCurrency(apiResp?.profitPerBed),
      sub: `${formatCurrency(apiResp?.profitPerBedOccupiedOnly)} (Occupied)`,
    },
    {
      title: "Revenue per Tenant",
      value: formatCurrency(apiResp?.revenuePerTenant),
    },
    {
      title: "Tenant Acquisition Cost",
      value: formatCurrency(apiResp?.tenantAcquisitionCost),
    },
  ];

  const propertyMetrics = [
    {
      title: "Avg. Tenant Stay",
      value: `${apiResp?.avgTenantDuration || 0} days`,
    },
    {
      title: "Occupancy Rate",
      value: formatPercent(apiResp?.occupancyRate),
    },
    {
      title: "Vacancy Rate",
      value: formatPercent(apiResp?.vacancyRate),
    },
  ];

  /* ================= Resize ================= */

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const hasSingleRevenuePoint =
    (apiResp?.monthlyRevenuePerTenant?.length || 0) === 1;

  const hasSingleProfitPoint =
    (apiResp?.monthlyProfitPerBed?.length || 0) === 1;

  const revenueChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
    },
    scales: {
      x: {
        offset: hasSingleRevenuePoint, // 👈 only when single month
      },
      y: {
        ticks: {
          callback: (value) => `₹${value}`,
        },
      },
    },
  };

  const profitChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
    },
    scales: {
      x: {
        offset: hasSingleProfitPoint,
      },
      y: {
        ticks: {
          callback: (value) => `₹${value}`,
        },
      },
    },
  };

  // const chartOptions = {
  //   responsive: true,
  //   maintainAspectRatio: false,
  //   elements: {
  //     point: {
  //       radius: 4,
  //       hoverRadius: 6,
  //     },
  //   },
  //   plugins: {
  //     legend: {
  //       position: "top",
  //     },
  //   },
  //   scales: {
  //     x: {
  //       offset: true,
  //       grid: { offset: true },
  //     },
  //     y: {
  //       ticks: {
  //         callback: (value) => `₹${value}`,
  //       },
  //     },
  //   },
  // };

  return (
    <Container>
      <div className="flex items-center justify-between bg-white py-4 px-5 shadow-md bg-gradient-to-r from-primary-color to-[#00A0FF]">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-11 w-11 rounded-2xl bg-[#EEF6FF] flex items-center justify-center shrink-0">
            <div className="text-[#0078BD] text-lg">
              <FiClipboard />
            </div>
          </div>
          <div className="min-w-0 text-white">
            <h1 className="text-lg font-semibold truncate text-white">Analytics</h1>
            <DateLabel onClick={() => setShowDatePicker(true)}>
              {moment(startDate).format("DD MMM yyyy")} – {moment(endDate).format("DD MMM yyyy")}
            </DateLabel>
          </div>
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleClose}
            className="h-10 w-10 rounded-xl hover:bg-primary-color/40 flex items-center justify-center transition-all duration-200 text-white">
            <MdClose size={20} />
          </button>
        </div>
      </div>

      {(isLoading || isRefetching) && (
        <div className="flex justify-center items-center my-40">
          <CircularProgress size={40} />
        </div>
      )}
      {!isLoading && !isRefetching && (

        <div className="custom-scrollbar flex flex-col px-4">
          <ScrollableContent className="flex-grow overflow-y-auto max-h-[calc(100dvh-100px)] custom-scrollbar pb-10 scrollbar-hide mt-4">
            <Content>
              {/* <div className="sticky top-0 bg-white z-10">
              <DateRange
                submitHandler={submitHandler}
                defaultLabel={`${moment(startDate).format("DD MMM yyyy")} - ${moment(endDate).format("DD MMM yyyy")}`}
              />
            </div> */}
              {/* ===== Loading ===== */}
              {loading ? (
                <div className="flex justify-center py-16">
                  <CircularProgress size={28} />
                </div>
              ) : error ? (
                <div className="text-center text-red-500 py-10">
                  Failed to load analytics.
                  <button
                    onClick={getListMutate}
                    className="ml-3 text-blue-600 underline"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                // tenantMetrics.map((item, index) => (
                //   <Card key={index}>
                //     <CardLeft>
                //       <CardTitle>{item.title}</CardTitle>
                //       {item.trends && (
                //         <TrendRow>
                //           {item.trends.map((trend, i) => (
                //             <span
                //               key={i}
                //               className={`text-xs font-normal ${trend.color}`}
                //             >
                //               {trend.arrow} {trend.value}% {trend.label}
                //             </span>
                //           ))}
                //         </TrendRow>
                //       )}
                //       {item.sub && <SubText>{item.sub}</SubText>}
                //     </CardLeft>
                //     <CardValue>{item.value}</CardValue>
                //   </Card>
                // ))
                <Content>

                  {/* Tenant Metrics */}

                  {tenantMetrics.map((item, index) => (
                    <Card key={index}>
                      <CardLeft>
                        <CardTitle>{item.title}</CardTitle>

                        {item.trends && (
                          <TrendRow>
                            {item.trends.map((trend, i) => (
                              <span key={i} className={`text-xs ${trend.color}`}>
                                {trend.arrow} {trend.value}% {trend.label}
                              </span>
                            ))}
                          </TrendRow>
                        )}

                        {item.sub && <SubText>{item.sub}</SubText>}
                      </CardLeft>

                      <CardValue>{item.value}</CardValue>
                    </Card>
                  ))}

                  {/* Financial Metrics */}

                  {financialMetrics.map((item, index) => (
                    <Card key={index}>
                      <CardLeft>
                        <CardTitle>{item.title}</CardTitle>
                        {item.sub && <SubText>{item.sub}</SubText>}
                      </CardLeft>

                      <CardValue>{item.value}</CardValue>
                    </Card>
                  ))}

                  {/* Property Metrics */}

                  {propertyMetrics.map((item, index) => (
                    <Card key={index}>
                      <CardLeft>
                        <CardTitle>{item.title}</CardTitle>
                        {item.sub && <SubText>{item.sub}</SubText>}
                      </CardLeft>

                      <CardValue>{item.value}</CardValue>
                    </Card>
                  ))}

                  {/* Revenue Chart */}

                  <ChartCard>
                    <ChartTitle>Monthly Revenue per Tenant</ChartTitle>

                    <ChartWrapper>
                      <Line data={revenueChartData} options={revenueChartOptions} />
                    </ChartWrapper>
                  </ChartCard>

                  {/* Profit Chart */}

                  <ChartCard>
                    <ChartTitle>Per Bed Profit Analysis</ChartTitle>

                    <ChartWrapper>
                      <Line data={profitChartData} options={profitChartOptions} />
                    </ChartWrapper>
                  </ChartCard>


                </Content>
              )}
            </Content>
          </ScrollableContent>

          <DateRangePicker
            show={showDatePicker}
            onClose={() => setShowDatePicker(false)}
            showOnlyMonths={true}
            initialStartDate={new Date(startDate)}
            initialEndDate={new Date(endDate)}
            submitHandler={submitHandler}
          />
        </div>)
      }
    </Container>
  );
};

/* ================= Styled ================= */

const Container = tw.div`
  bg-white w-full h-full flex flex-col relative overflow-hidden
`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4 text-white`;

const Content = tw.div`flex flex-col gap-4`;

const Card = tw.div`
  bg-white rounded-xl border p-5
  shadow-sm flex justify-between items-start
`;

const CardLeft = tw.div`flex flex-col`;
const CardTitle = tw.p`text-md text-gray-700 font-medium`;
const CardValue = tw.p`text-lg font-semibold text-gray-800`;
const SubText = tw.p`text-sm text-gray-500 mt-2`;
const TrendRow = tw.div`
  flex gap-3 mt-1.5 items-center
`;

const ChartCard = tw.div`
bg-white rounded-xl border p-5 shadow-sm
`;

const ChartTitle = tw.p`
text-sm font-semibold text-gray-700 mb-4
`;

const ChartWrapper = tw.div`
h-[260px]
`;

const DateLabel = tw.span`text-xs opacity-80 cursor-pointer`;

export default AnalyticsSideModal;